// Type definitions
export interface DeviceSettings {
	[deviceId: string]: any;
}

export interface PresenceZone {
	id: string;
	title: string;
	value: string;
}

export interface DeviceProp {
	title: string;
	value: string;
	disabled: boolean;
}

export interface ConditionResult {
	conditionName: string;
	conditionValue: string | number;
	conditionOperator: string;
	zoneAddInfoInd?: string;
}

/**
 * Check if the given device is a Presence Device (S4SN-0U61X)
 */
export function isPresenceDevice(deviceId: string | undefined, devices: any): boolean {
	if (!deviceId) return false;

	// Extract the real device ID (remove channel if present)
	const realDeviceId = deviceId.split('_')[0];
	return devices[realDeviceId]?.type === 'S4SN-0U61X';
}

/**
 * Extract zones from presence device settings
 */
export function getPresenceZones(
	deviceId: string | undefined,
	deviceSettings: DeviceSettings,
): PresenceZone[] {
	if (!deviceId || !deviceSettings) return [];

	// Extract the real device ID (remove channel if present)
	const realDeviceId = deviceId.split('_')[0];
	const settings = deviceSettings[realDeviceId];
	if (!settings) return [];

	const zones: PresenceZone[] = [];

	// Extract zones from settings (presencezone:XXX)
	Object.keys(settings).forEach((key) => {
		const match = key.match(/^presencezone:(\d+)$/);
		if (match) {
			// Include all zones including zone 200
			const zoneId = match[1];
			const zoneData = settings[key];
			if (zoneData && zoneData.name) {
				zones.push({
					id: zoneId,
					title: zoneData.name,
					value: `zone_${zoneId}`,
				});
			}
		}
	});

	// Sort zones by ID in ascending order
	zones.sort((a, b) => parseInt(a.id) - parseInt(b.id));

	return zones;
}

/**
 * Get effective device properties for presence devices
 */
export function getEffectivePresenceDeviceProps(
	formatFn: any,
	presenceZones: PresenceZone[],
): DeviceProp[] {
	return [
		{
			title: formatFn('zone_detection', { default: 'Zone Detection' }),
			value: 'zone_detection',
			disabled: presenceZones.length === 0,
		},
		{
			title: formatFn('luminosity', { default: 'Luminosity' }),
			value: 'luminosity',
			disabled: false,
		},
	];
}

/**
 * Validate presence device condition parameters
 */
export function validatePresenceCondition(
	selectedPresenceMode: string,
	selectedZone: string,
	selectedZoneProperty: string,
): boolean {
	if (selectedPresenceMode === 'zone_detection') {
		// Zone detection requires zone and property selection
		return Boolean(selectedZone && selectedZoneProperty);
	} else if (selectedPresenceMode === 'luminosity') {
		// Luminosity requires property selection
		return Boolean(selectedZoneProperty);
	} else if (selectedPresenceMode === 'illuminance_threshold') {
		return (
			Boolean(selectedZoneProperty) &&
			(selectedZoneProperty === 'lt' || selectedZoneProperty === 'gt')
		);
	}
	return false;
}

/**
 * Build presence condition based on selected parameters
 */
export function buildPresenceCondition(
	selectedPresenceMode: string,
	selectedZone: string,
	selectedZoneProperty: string,
	conditionValue: string | number,
): ConditionResult | null {
	let conditionName = '';
	let processedValue: string | number = '';
	let conditionOperator = 'eq';
	let zoneAddInfoInd: string | undefined = undefined;

	if (selectedPresenceMode === 'zone_detection' && selectedZone) {
		// Extract zone ID from selectedZone (format: zone_XXX)
		const zoneId = selectedZone.replace('zone_', '');

		if (selectedZoneProperty === 'presence' || selectedZoneProperty === 'absence') {
			conditionName = 'presence_zone_state'; // Use UI naming convention
			processedValue = selectedZoneProperty === 'presence' ? 'true' : 'false';
			conditionOperator = 'eq';
		} else if (selectedZoneProperty === 'enter' || selectedZoneProperty === 'leave') {
			conditionName = 'presence_zone_objects_delta'; // Delta-based enter/leave detection
			processedValue = 0;
			conditionOperator = selectedZoneProperty === 'enter' ? 'gt' : 'lt';
		} else if (selectedZoneProperty === 'object_count') {
			conditionName = 'presence_zone_objects'; // Use UI naming convention
			// Use provided input value or default to 1
			processedValue = Number(conditionValue) || 1;
			conditionOperator = 'eq';
		}

		// Set the zone ID as add_info_ind for proper transformation
		zoneAddInfoInd = zoneId;
	} else if (selectedPresenceMode === 'luminosity') {
		// Use discrete Dark / Twilight / Bright band (maps to scene command `lux` like other sensors)
		conditionName = 'luxury';
		processedValue = String(conditionValue); // 'dark' | 'twilight' | 'bright'
		conditionOperator = 'eq';
	} else if (selectedPresenceMode === 'illuminance_threshold') {
		// Numeric lux threshold — same as `_ILLUMINATION_NUMBER` / `lux_i` on other sensors
		conditionName = 'illumination';
		processedValue = Number(conditionValue);
		conditionOperator = selectedZoneProperty === 'gt' ? 'gt' : 'lt';
	}

	if (conditionName) {
		return {
			conditionName,
			conditionValue: processedValue,
			conditionOperator,
			zoneAddInfoInd,
		};
	}

	return null;
}
